/* design-rescue-max-lines-ok: justification: all locale descriptor fields resolve at one strict missing-copy boundary; split follow-up ForestModeler-74tv8 */
/* eslint-disable max-lines, max-lines-per-function -- Locale descriptor fields resolve at one strict missing-copy boundary. */
import type { Translations } from '@/i18n';

interface CatalogCopy {
  name: string;
  summary: string;
  teaser: string;
  includes: readonly [string, ...string[]];
  actionLabel: string;
}

export interface LandingProductCopy {
  chrome: {
    features: string;
    howItWorks: string;
    pricing: string;
    login: string;
    register: string;
    logout: string;
    dashboard: string;
    account: string;
    navigationAriaLabel: string;
    menuAriaLabel: string;
    languageAriaLabel: string;
    openNavigation: string;
    closeNavigation: string;
  };
  hero: {
    title: string;
    summary: string;
    searchPlaceholder: string;
    searchSubmit: string;
    searchHelper: string;
    benefits: readonly [string, string, string];
    forecast: {
      propertyLabel: string;
      modelledLabel: string;
      growthLabel: string;
      harvestIncomeLabel: string;
      growthNavLabel: string;
      choicesNavLabel: string;
      incomeNavLabel: string;
      loadingLabel: string;
      loadingDetail: string;
      unavailableLabel: string;
      unavailableDetail: string;
    };
    proofPoints: readonly [string, string, string];
  };
  understand: { title: string; sectionTitle: string; answer: string; action: string };
  showcase: {
    mapTitle: string;
    mapLabel: string;
    mapDescription: string;
    mapCta: string;
    mapTags: readonly [string, string];
    mapSelectedLabel: string;
    mapSelectedSource: string;
    mapSelectedHint: string;
    measurementLabel: string;
    measurementValue: string;
    measurementProof: string;
    measurementDescription: string;
    measurementCta: string;
    planningLabel: string;
    planningDescription: string;
    planningCta: string;
  };
  valueStreams: {
    title: string;
    summary: string;
    buyer: {
      title: string;
      description: string;
      featureAnswer: string;
      featureDescription: string;
      featureCaption: string;
      tags: readonly [string, string];
      cta: string;
    };
    value: { title: string; description: string; tags: readonly [string, string]; cta: string };
    measurement: {
      title: string;
      description: string;
      tags: readonly [string, string];
      cta: string;
    };
    planning: { title: string; description: string; tags: readonly [string, string]; cta: string };
    simulation: {
      title: string;
      summary: string;
      group: string;
      criterion: string;
      focus: string;
      question: string;
      lenses: readonly [
        { title: string; focus: string; question: string },
        { title: string; focus: string; question: string },
        { title: string; focus: string; question: string },
        { title: string; focus: string; question: string },
      ];
    };
    control: {
      chapterTitle: string;
      title: string;
      summary: string;
      askTitle: string;
      askDescription: string;
      askCta: string;
      bestOfferLabel: string;
      nextOfferGapLabel: string;
      marketGapLabel: string;
      assistantSourceLabel: string;
      assistantSourceValue: string;
      assistantFollowupLabel: string;
      assistantFollowupValue: string;
    };
  };
  confidence: { title: string; sectionTitle: string; answer: string; action: string };
  compare: { title: string; sectionTitle: string; answer: string; action: string };
  act: { title: string; sectionTitle: string; answer: string; action: string };
  informed: { title: string; sectionTitle: string; answer: string; action: string };
  start: { title: string; sectionTitle: string; answer: string; free: CatalogCopy };
  offerDecode: CatalogCopy;
  plotPackage: CatalogCopy;
  buyerDecode: CatalogCopy;
  estate: CatalogCopy;
  expert: {
    source: { title: string; answer: string; actionContext: string };
    measurement: {
      title: string;
      answer: string;
      actionContext: string;
      sourceMetsakeskus: string;
      sourceFieldScan: string;
      sourceManual: string;
      sourceUnknown: string;
      readinessReady: string;
      publicReady: string;
      addWhenNeeded: string;
      refinedResult: string;
    };
    planning: {
      title: string;
      answer: string;
      preferences: string;
      comparison: string;
      visualReview: string;
      confirmation: string;
      exportSelection: string;
      status: string;
      actionContext: string;
      exampleCriteria: string;
      optionThin: string;
      optionThinSummary: string;
      optionWait: string;
      optionWaitSummary: string;
      incomeTiming: string;
      natureValues: string;
      earlier: string;
      later: string;
      preserved: string;
    };
    offer: {
      title: string;
      answer: string;
      freeStatus: string;
      paidStatus: string;
      confirmed: string;
      needsConfirmation: string;
      noFields: string;
      actionContext: string;
      uploadLabel: string;
      verifyLabel: string;
      compareLabel: string;
      decideLabel: string;
      availableLabel: string;
      afterAccountLabel: string;
      ownerLabel: string;
    };
    monitoring: {
      title: string;
      answer: string;
      check: string;
      change: string;
      notify: string;
      status: string;
      actionContext: string;
      sourcesValue: string;
      changesValue: string;
      notificationValue: string;
    };
  };
  state: {
    loading: string;
    error: string;
    empty: string;
    retry: string;
    partial: string;
    locked: string;
    stale: string;
    sourceStaleLabel: string;
    sourceStaleDetail: string;
    sourceUnavailableDetail: string;
    valuePriceSourceName: string;
  };
  labels: {
    page: string;
    property: string;
    area: string;
    stands: string;
    sources: string;
    dates: string;
    missing: string;
    publicFields: string;
    measuredStands: string;
    changedFields: string;
    operations: string;
    updated: string;
    catalogUpdated: string;
    annual: string;
    oneOff: string;
    funnelIncludesYear: string;
    monitor: string;
    observed: string;
    served: string;
    roughValueUnavailable: string;
    roughValueCoverage: string;
    roughValuePriceSourceName: string;
  };
  footer: {
    description: string;
    legalAriaLabel: string;
    termsLabel: string;
    privacyLabel: string;
    contactLabel: string;
    contactEmail: string;
    buyerLabel: string;
    allRightsReserved: string;
  };
}

function text(value: string | undefined, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`landing.${path} must contain localized product-story copy`);
  }
  return value;
}

function resolveLandingStateCopy(
  t: Translations,
  landing: NonNullable<Translations['landing']>,
): LandingProductCopy['state'] {
  return {
    loading: text(t.common.loading, 'common.loading'),
    error: text(t.common.error, 'common.error'),
    empty: text(t.common.noDataAvailable, 'common.noDataAvailable'),
    retry: text(t.common.tryAgain, 'common.tryAgain'),
    partial: text(landing.mapStory?.footer, 'mapStory.footer'),
    locked: text(landing.cta?.onRamp, 'cta.onRamp'),
    stale: text(landing.ownerPricing?.priceLoadsAtCheckout, 'ownerPricing.priceLoadsAtCheckout'),
    sourceStaleLabel: text(landing.mapStory?.sourceStaleLabel, 'mapStory.sourceStaleLabel'),
    sourceStaleDetail: text(landing.mapStory?.sourceStaleDetail, 'mapStory.sourceStaleDetail'),
    sourceUnavailableDetail: text(
      landing.mapStory?.sourceUnavailableDetail,
      'mapStory.sourceUnavailableDetail',
    ),
    valuePriceSourceName: text(landing.mapStory?.valuePriceSourceName, 'mapStory.valuePriceSourceName'),
  };
}

function list(value: string[] | undefined, path: string): readonly [string, ...string[]] {
  if (!Array.isArray(value) || value.length === 0 || value.some((item) => item.trim() === '')) {
    throw new Error(`landing.${path} must contain localized product-story items`);
  }
  return value as [string, ...string[]];
}

function catalogCopy(
  value:
    | {
        name?: string;
        summary?: string;
        teaser?: string;
        features?: string[];
        cta?: string;
      }
    | undefined,
  path: string,
): CatalogCopy {
  return {
    name: text(value?.name, `${path}.name`),
    summary: text(value?.summary, `${path}.summary`),
    teaser: text(value?.teaser, `${path}.teaser`),
    includes: list(value?.features, `${path}.features`),
    actionLabel: text(value?.cta, `${path}.cta`),
  };
}

function secondaryCatalogCopy(
  value:
    | {
        title?: string;
        unlocksSummary?: string;
        teaser?: string;
        features?: string[];
        ctaLabel?: string;
      }
    | undefined,
  path: string,
): CatalogCopy {
  return {
    name: text(value?.title, `${path}.title`),
    summary: text(value?.unlocksSummary, `${path}.unlocksSummary`),
    teaser: text(value?.teaser, `${path}.teaser`),
    includes: list(value?.features, `${path}.features`),
    actionLabel: text(value?.ctaLabel, `${path}.ctaLabel`),
  };
}

// Optional translation branches are validated by text(); the branches do not
// select behavior and deliberately fail closed when locale content is absent.
// eslint-disable-next-line complexity
function resolveLandingLabelsCopy(
  t: Translations,
  landing: NonNullable<Translations['landing']>,
): LandingProductCopy['labels'] {
  const { hero, ownerPricing: owner } = landing;
  return {
    page: text(hero?.title, 'hero.title'),
    property: text(t.common.properties, 'common.properties'),
    area: text(t.common.area, 'common.area'),
    stands: text(t.common.stands, 'common.stands'),
    sources: text(owner?.storyLabels?.sources, 'ownerPricing.storyLabels.sources'),
    dates: text(owner?.storyLabels?.sourceDates, 'ownerPricing.storyLabels.sourceDates'),
    missing: text(t.common.noDataAvailable, 'common.noDataAvailable'),
    publicFields: text(landing.mapStory?.includeBoundary, 'mapStory.includeBoundary'),
    measuredStands: text(landing.howItWorks?.step2?.title, 'howItWorks.step2.title'),
    changedFields: text(
      owner?.storyLabels?.changedFields,
      'ownerPricing.storyLabels.changedFields',
    ),
    operations: text(owner?.storyLabels?.operations, 'ownerPricing.storyLabels.operations'),
    updated: text(owner?.storyLabels?.updated, 'ownerPricing.storyLabels.updated'),
    catalogUpdated: text(owner?.catalogUpdatedLabel, 'ownerPricing.catalogUpdatedLabel'),
    annual: text(owner?.annualLabel, 'ownerPricing.annualLabel'),
    oneOff: text(owner?.oneOffLabel, 'ownerPricing.oneOffLabel'),
    funnelIncludesYear: text(owner?.funnelIncludesYear, 'ownerPricing.funnelIncludesYear'),
    monitor: text(owner?.storyLabels?.monitor, 'ownerPricing.storyLabels.monitor'),
    observed: text(owner?.storyLabels?.observed, 'ownerPricing.storyLabels.observed'),
    served: text(owner?.storyLabels?.served, 'ownerPricing.storyLabels.served'),
    roughValueUnavailable: text(
      t.common.propertyBrowse.valueRangeUnavailable,
      'common.propertyBrowse.valueRangeUnavailable',
    ),
    roughValueCoverage: text(landing.mapStory?.valueCoverageLabel, 'mapStory.valueCoverageLabel'),
    roughValuePriceSourceName: text(
      landing.mapStory?.valuePriceSourceName,
      'mapStory.valuePriceSourceName',
    ),
  };
}

// Optional translation branches produce a high mechanical complexity score;
// every branch is fail-closed through text()/list(), not behavioral control flow.
// eslint-disable-next-line complexity
export function resolveLandingProductCopy(t: Translations): LandingProductCopy {
  const landing = t.landing;
  const { header, hero, features, ownerPricing: owner } = landing ?? {};
  if (!landing || !header || !hero || !features || !owner) {
    throw new Error('landing translations are required for the product route');
  }
  return {
    chrome: {
      features: text(header.features, 'header.features'),
      howItWorks: text(header.howItWorks, 'header.howItWorks'),
      pricing: text(header.pricing, 'header.pricing'),
      login: text(header.login, 'header.login'),
      register: text(header.register, 'header.register'),
      logout: text(header.logout, 'header.logout'),
      dashboard: text(header.dashboard, 'header.dashboard'),
      account: text(header.account, 'header.account'),
      navigationAriaLabel: text(header.navAriaLabel, 'header.navAriaLabel'),
      menuAriaLabel: text(header.navMenuAriaLabel, 'header.navMenuAriaLabel'),
      languageAriaLabel: text(header.languageAriaLabel, 'header.languageAriaLabel'),
      openNavigation: text(header.openNavigation, 'header.openNavigation'),
      closeNavigation: text(header.closeNavigation, 'header.closeNavigation'),
    },
    hero: {
      title: text(hero.title, 'hero.title'),
      summary: text(hero.subline, 'hero.subline'),
      searchPlaceholder: text(hero.propertySearch?.placeholder, 'hero.propertySearch.placeholder'),
      searchSubmit: text(hero.propertySearch?.button, 'hero.propertySearch.button'),
      searchHelper: text(hero.propertySearch?.helper, 'hero.propertySearch.helper'),
      benefits: [
        text(hero.benefits?.value, 'hero.benefits.value'),
        text(hero.benefits?.compare, 'hero.benefits.compare'),
        text(hero.benefits?.decide, 'hero.benefits.decide'),
      ],
      forecast: {
        propertyLabel: text(hero.forecast?.propertyLabel, 'hero.forecast.propertyLabel'),
        modelledLabel: text(hero.forecast?.modelledLabel, 'hero.forecast.modelledLabel'),
        growthLabel: text(hero.forecast?.growthLabel, 'hero.forecast.growthLabel'),
        harvestIncomeLabel: text(hero.forecast?.harvestIncomeLabel, 'hero.forecast.harvestIncomeLabel'),
        growthNavLabel: text(hero.forecast?.growthNavLabel, 'hero.forecast.growthNavLabel'),
        choicesNavLabel: text(hero.forecast?.choicesNavLabel, 'hero.forecast.choicesNavLabel'),
        incomeNavLabel: text(hero.forecast?.incomeNavLabel, 'hero.forecast.incomeNavLabel'),
        loadingLabel: text(hero.forecast?.loadingLabel, 'hero.forecast.loadingLabel'),
        loadingDetail: text(hero.forecast?.loadingDetail, 'hero.forecast.loadingDetail'),
        unavailableLabel: text(hero.forecast?.unavailableLabel, 'hero.forecast.unavailableLabel'),
        unavailableDetail: text(hero.forecast?.unavailableDetail, 'hero.forecast.unavailableDetail'),
      },
      proofPoints: [
        text(hero.benefits?.value, 'hero.benefits.value'),
        text(hero.benefits?.compare, 'hero.benefits.compare'),
        text(hero.benefits?.decide, 'hero.benefits.decide'),
      ],
    },
    understand: {
      title: text(header.features, 'header.features'),
      sectionTitle: text(landing.mapStory?.eyebrow, 'mapStory.eyebrow'),
      answer: text(landing.mapStory?.body, 'mapStory.body'),
      action: text(owner.freeCockpit?.cta, 'ownerPricing.freeCockpit.cta'),
    },
    showcase: {
      mapTitle: text(landing.mapStory?.title, 'mapStory.title'),
      mapLabel: text(landing.mapStory?.mapFeatureLabel, 'mapStory.mapFeatureLabel'),
      mapDescription: text(
        landing.mapStory?.mapFeatureDescription,
        'mapStory.mapFeatureDescription',
      ),
      mapCta: text(landing.mapStory?.mapFeatureCta, 'mapStory.mapFeatureCta'),
      mapTags: [
        text(landing.mapStory?.mapFeatureTagDetails, 'mapStory.mapFeatureTagDetails'),
        text(landing.mapStory?.mapFeatureTagZoom, 'mapStory.mapFeatureTagZoom'),
      ],
      mapSelectedLabel: text(landing.mapStory?.mapSelectedLabel, 'mapStory.mapSelectedLabel'),
      mapSelectedSource: text(landing.mapStory?.mapSelectedSource, 'mapStory.mapSelectedSource'),
      mapSelectedHint: text(landing.mapStory?.mapSelectedHint, 'mapStory.mapSelectedHint'),
      measurementLabel: text(
        landing.mapStory?.measurementFeatureLabel,
        'mapStory.measurementFeatureLabel',
      ),
      measurementValue: text(
        landing.mapStory?.measurementFeatureValue,
        'mapStory.measurementFeatureValue',
      ),
      measurementProof: text(
        landing.mapStory?.measurementFeatureProof,
        'mapStory.measurementFeatureProof',
      ),
      measurementDescription: text(
        landing.mapStory?.measurementFeatureDescription,
        'mapStory.measurementFeatureDescription',
      ),
      measurementCta: text(
        landing.mapStory?.measurementFeatureCta,
        'mapStory.measurementFeatureCta',
      ),
      planningLabel: text(landing.mapStory?.planningFeatureLabel, 'mapStory.planningFeatureLabel'),
      planningDescription: text(
        landing.mapStory?.planningFeatureDescription,
        'mapStory.planningFeatureDescription',
      ),
      planningCta: text(landing.mapStory?.planningFeatureCta, 'mapStory.planningFeatureCta'),
    },
    valueStreams: {
      title: text(landing.mapStory?.valueStreams?.title, 'mapStory.valueStreams.title'),
      summary: text(landing.mapStory?.valueStreams?.summary, 'mapStory.valueStreams.summary'),
      buyer: {
        title: text(landing.mapStory?.valueStreams?.buyerTitle, 'mapStory.valueStreams.buyerTitle'),
        description: text(
          landing.mapStory?.valueStreams?.buyerDescription,
          'mapStory.valueStreams.buyerDescription',
        ),
        featureAnswer: text(
          landing.mapStory?.valueStreams?.buyerFeatureAnswer,
          'mapStory.valueStreams.buyerFeatureAnswer',
        ),
        featureDescription: text(
          landing.mapStory?.valueStreams?.buyerFeatureDescription,
          'mapStory.valueStreams.buyerFeatureDescription',
        ),
        featureCaption: text(
          landing.mapStory?.valueStreams?.buyerFeatureCaption,
          'mapStory.valueStreams.buyerFeatureCaption',
        ),
        tags: [
          text(
            landing.mapStory?.valueStreams?.buyerTagValue,
            'mapStory.valueStreams.buyerTagValue',
          ),
          text(
            landing.mapStory?.valueStreams?.buyerTagFuture,
            'mapStory.valueStreams.buyerTagFuture',
          ),
        ],
        cta: text(landing.mapStory?.valueStreams?.buyerCta, 'mapStory.valueStreams.buyerCta'),
      },
      value: {
        title: text(landing.mapStory?.valueStreams?.valueTitle, 'mapStory.valueStreams.valueTitle'),
        description: text(
          landing.mapStory?.valueStreams?.valueDescription,
          'mapStory.valueStreams.valueDescription',
        ),
        tags: [
          text(
            landing.mapStory?.valueStreams?.valueTagEstimate,
            'mapStory.valueStreams.valueTagEstimate',
          ),
          text(
            landing.mapStory?.valueStreams?.valueTagPrices,
            'mapStory.valueStreams.valueTagPrices',
          ),
        ],
        cta: text(landing.mapStory?.valueStreams?.valueCta, 'mapStory.valueStreams.valueCta'),
      },
      measurement: {
        title: text(
          landing.mapStory?.valueStreams?.measurementTitle,
          'mapStory.valueStreams.measurementTitle',
        ),
        description: text(
          landing.mapStory?.valueStreams?.measurementDescription,
          'mapStory.valueStreams.measurementDescription',
        ),
        tags: [
          text(
            landing.mapStory?.valueStreams?.measurementTagSpeed,
            'mapStory.valueStreams.measurementTagSpeed',
          ),
          text(
            landing.mapStory?.valueStreams?.measurementTagAccuracy,
            'mapStory.valueStreams.measurementTagAccuracy',
          ),
        ],
        cta: text(
          landing.mapStory?.valueStreams?.measurementCta,
          'mapStory.valueStreams.measurementCta',
        ),
      },
      planning: {
        title: text(
          landing.mapStory?.valueStreams?.planningTitle,
          'mapStory.valueStreams.planningTitle',
        ),
        description: text(
          landing.mapStory?.valueStreams?.planningDescription,
          'mapStory.valueStreams.planningDescription',
        ),
        tags: [
          text(
            landing.mapStory?.valueStreams?.planningTagSimulation,
            'mapStory.valueStreams.planningTagSimulation',
          ),
          text(
            landing.mapStory?.valueStreams?.planningTagProof,
            'mapStory.valueStreams.planningTagProof',
          ),
        ],
        cta: text(landing.mapStory?.valueStreams?.planningCta, 'mapStory.valueStreams.planningCta'),
      },
      simulation: {
        title: text(
          landing.mapStory?.valueStreams?.simulationTitle,
          'mapStory.valueStreams.simulationTitle',
        ),
        summary: text(
          landing.mapStory?.valueStreams?.simulationSummary,
          'mapStory.valueStreams.simulationSummary',
        ),
        group: text(
          landing.mapStory?.valueStreams?.simulationGroup,
          'mapStory.valueStreams.simulationGroup',
        ),
        criterion: text(
          landing.mapStory?.valueStreams?.simulationCriterion,
          'mapStory.valueStreams.simulationCriterion',
        ),
        focus: text(
          landing.mapStory?.valueStreams?.simulationFocus,
          'mapStory.valueStreams.simulationFocus',
        ),
        question: text(
          landing.mapStory?.valueStreams?.simulationQuestion,
          'mapStory.valueStreams.simulationQuestion',
        ),
        lenses: [
          {
            title: text(
              landing.mapStory?.valueStreams?.lensValue,
              'mapStory.valueStreams.lensValue',
            ),
            focus: text(
              landing.mapStory?.valueStreams?.lensValueFocus,
              'mapStory.valueStreams.lensValueFocus',
            ),
            question: text(
              landing.mapStory?.valueStreams?.lensValueQuestion,
              'mapStory.valueStreams.lensValueQuestion',
            ),
          },
          {
            title: text(
              landing.mapStory?.valueStreams?.lensIncome,
              'mapStory.valueStreams.lensIncome',
            ),
            focus: text(
              landing.mapStory?.valueStreams?.lensIncomeFocus,
              'mapStory.valueStreams.lensIncomeFocus',
            ),
            question: text(
              landing.mapStory?.valueStreams?.lensIncomeQuestion,
              'mapStory.valueStreams.lensIncomeQuestion',
            ),
          },
          {
            title: text(
              landing.mapStory?.valueStreams?.lensContinuous,
              'mapStory.valueStreams.lensContinuous',
            ),
            focus: text(
              landing.mapStory?.valueStreams?.lensContinuousFocus,
              'mapStory.valueStreams.lensContinuousFocus',
            ),
            question: text(
              landing.mapStory?.valueStreams?.lensContinuousQuestion,
              'mapStory.valueStreams.lensContinuousQuestion',
            ),
          },
          {
            title: text(
              landing.mapStory?.valueStreams?.lensGuidelines,
              'mapStory.valueStreams.lensGuidelines',
            ),
            focus: text(
              landing.mapStory?.valueStreams?.lensGuidelinesFocus,
              'mapStory.valueStreams.lensGuidelinesFocus',
            ),
            question: text(
              landing.mapStory?.valueStreams?.lensGuidelinesQuestion,
              'mapStory.valueStreams.lensGuidelinesQuestion',
            ),
          },
        ],
      },
      control: {
        chapterTitle: text(
          landing.mapStory?.valueStreams?.controlChapterTitle,
          'mapStory.valueStreams.controlChapterTitle',
        ),
        title: text(
          landing.mapStory?.valueStreams?.controlTitle,
          'mapStory.valueStreams.controlTitle',
        ),
        summary: text(
          landing.mapStory?.valueStreams?.controlSummary,
          'mapStory.valueStreams.controlSummary',
        ),
        askTitle: text(landing.mapStory?.valueStreams?.askTitle, 'mapStory.valueStreams.askTitle'),
        askDescription: text(
          landing.mapStory?.valueStreams?.askDescription,
          'mapStory.valueStreams.askDescription',
        ),
        askCta: text(landing.mapStory?.valueStreams?.askCta, 'mapStory.valueStreams.askCta'),
        bestOfferLabel: text(
          landing.mapStory?.valueStreams?.bestOfferLabel,
          'mapStory.valueStreams.bestOfferLabel',
        ),
        nextOfferGapLabel: text(
          landing.mapStory?.valueStreams?.nextOfferGapLabel,
          'mapStory.valueStreams.nextOfferGapLabel',
        ),
        marketGapLabel: text(
          landing.mapStory?.valueStreams?.marketGapLabel,
          'mapStory.valueStreams.marketGapLabel',
        ),
        assistantSourceLabel: text(
          landing.mapStory?.valueStreams?.assistantSourceLabel,
          'mapStory.valueStreams.assistantSourceLabel',
        ),
        assistantSourceValue: text(
          landing.mapStory?.valueStreams?.assistantSourceValue,
          'mapStory.valueStreams.assistantSourceValue',
        ),
        assistantFollowupLabel: text(
          landing.mapStory?.valueStreams?.assistantFollowupLabel,
          'mapStory.valueStreams.assistantFollowupLabel',
        ),
        assistantFollowupValue: text(
          landing.mapStory?.valueStreams?.assistantFollowupValue,
          'mapStory.valueStreams.assistantFollowupValue',
        ),
      },
    },
    confidence: {
      title: text(features.feature6?.title, 'features.feature6.title'),
      sectionTitle: text(landing.expert?.measurement?.title, 'expert.measurement.title'),
      answer: text(features.feature6?.description, 'features.feature6.description'),
      action: text(landing.expert?.measurement?.title, 'expert.measurement.title'),
    },
    compare: {
      title: text(features.feature3?.title, 'features.feature3.title'),
      sectionTitle: text(features.feature4?.title, 'features.feature4.title'),
      answer: text(features.feature3?.description, 'features.feature3.description'),
      action: text(features.feature4?.title, 'features.feature4.title'),
    },
    act: {
      title: text(landing.expert?.offer?.title, 'expert.offer.title'),
      sectionTitle: text(owner.offerDecode?.name, 'ownerPricing.offerDecode.name'),
      answer: text(owner.offerDecode?.summary, 'ownerPricing.offerDecode.summary'),
      action: text(owner.offerDecode?.cta, 'ownerPricing.offerDecode.cta'),
    },
    informed: {
      title: text(features.feature5?.title, 'features.feature5.title'),
      sectionTitle: text(features.feature5?.badge, 'features.feature5.badge'),
      answer: text(features.feature5?.description, 'features.feature5.description'),
      action: text(features.feature5?.badge, 'features.feature5.badge'),
    },
    start: {
      title: text(landing.pricing?.title, 'pricing.title'),
      sectionTitle: text(owner.freeCockpit?.eyebrow, 'ownerPricing.freeCockpit.eyebrow'),
      answer: text(landing.pricing?.subtitle, 'pricing.subtitle'),
      free: catalogCopy(owner.freeCockpit, 'ownerPricing.freeCockpit'),
    },
    offerDecode: catalogCopy(owner.offerDecode, 'ownerPricing.offerDecode'),
    plotPackage: catalogCopy(owner.plotPackage, 'ownerPricing.plotPackage'),
    buyerDecode: secondaryCatalogCopy(owner.buyerDecode, 'ownerPricing.buyerDecode'),
    estate: secondaryCatalogCopy(owner.estate, 'ownerPricing.estate'),
    expert: {
      source: {
        title: text(landing.expert?.source?.title, 'expert.source.title'),
        answer: text(landing.expert?.source?.answer, 'expert.source.answer'),
        actionContext: text(landing.expert?.source?.actionContext, 'expert.source.actionContext'),
      },
      measurement: {
        title: text(landing.expert?.measurement?.title, 'expert.measurement.title'),
        answer: text(landing.expert?.measurement?.answer, 'expert.measurement.answer'),
        actionContext: text(
          landing.expert?.measurement?.actionContext,
          'expert.measurement.actionContext',
        ),
        sourceMetsakeskus: text(
          landing.expert?.measurement?.sourceMetsakeskus,
          'expert.measurement.sourceMetsakeskus',
        ),
        sourceFieldScan: text(
          landing.expert?.measurement?.sourceFieldScan,
          'expert.measurement.sourceFieldScan',
        ),
        sourceManual: text(
          landing.expert?.measurement?.sourceManual,
          'expert.measurement.sourceManual',
        ),
        sourceUnknown: text(
          landing.expert?.measurement?.sourceUnknown,
          'expert.measurement.sourceUnknown',
        ),
        readinessReady: text(
          landing.expert?.measurement?.readinessReady,
          'expert.measurement.readinessReady',
        ),
        publicReady: text(
          landing.expert?.measurement?.publicReady,
          'expert.measurement.publicReady',
        ),
        addWhenNeeded: text(
          landing.expert?.measurement?.addWhenNeeded,
          'expert.measurement.addWhenNeeded',
        ),
        refinedResult: text(
          landing.expert?.measurement?.refinedResult,
          'expert.measurement.refinedResult',
        ),
      },
      planning: {
        title: text(landing.expert?.planning?.title, 'expert.planning.title'),
        answer: text(landing.expert?.planning?.answer, 'expert.planning.answer'),
        preferences: text(landing.expert?.planning?.preferences, 'expert.planning.preferences'),
        comparison: text(landing.expert?.planning?.comparison, 'expert.planning.comparison'),
        visualReview: text(landing.expert?.planning?.visualReview, 'expert.planning.visualReview'),
        confirmation: text(landing.expert?.planning?.confirmation, 'expert.planning.confirmation'),
        exportSelection: text(
          landing.expert?.planning?.exportSelection,
          'expert.planning.exportSelection',
        ),
        status: text(landing.expert?.planning?.status, 'expert.planning.status'),
        actionContext: text(
          landing.expert?.planning?.actionContext,
          'expert.planning.actionContext',
        ),
        exampleCriteria: text(
          landing.expert?.planning?.exampleCriteria,
          'expert.planning.exampleCriteria',
        ),
        optionThin: text(landing.expert?.planning?.optionThin, 'expert.planning.optionThin'),
        optionThinSummary: text(
          landing.expert?.planning?.optionThinSummary,
          'expert.planning.optionThinSummary',
        ),
        optionWait: text(landing.expert?.planning?.optionWait, 'expert.planning.optionWait'),
        optionWaitSummary: text(
          landing.expert?.planning?.optionWaitSummary,
          'expert.planning.optionWaitSummary',
        ),
        incomeTiming: text(landing.expert?.planning?.incomeTiming, 'expert.planning.incomeTiming'),
        natureValues: text(landing.expert?.planning?.natureValues, 'expert.planning.natureValues'),
        earlier: text(landing.expert?.planning?.earlier, 'expert.planning.earlier'),
        later: text(landing.expert?.planning?.later, 'expert.planning.later'),
        preserved: text(landing.expert?.planning?.preserved, 'expert.planning.preserved'),
      },
      offer: {
        title: text(landing.expert?.offer?.title, 'expert.offer.title'),
        answer: text(landing.expert?.offer?.answer, 'expert.offer.answer'),
        freeStatus: text(landing.expert?.offer?.freeStatus, 'expert.offer.freeStatus'),
        paidStatus: text(landing.expert?.offer?.paidStatus, 'expert.offer.paidStatus'),
        confirmed: text(landing.expert?.offer?.confirmed, 'expert.offer.confirmed'),
        needsConfirmation: text(
          landing.expert?.offer?.needsConfirmation,
          'expert.offer.needsConfirmation',
        ),
        noFields: text(landing.expert?.offer?.noFields, 'expert.offer.noFields'),
        actionContext: text(landing.expert?.offer?.actionContext, 'expert.offer.actionContext'),
        uploadLabel: text(landing.expert?.offer?.uploadLabel, 'expert.offer.uploadLabel'),
        verifyLabel: text(landing.expert?.offer?.verifyLabel, 'expert.offer.verifyLabel'),
        compareLabel: text(landing.expert?.offer?.compareLabel, 'expert.offer.compareLabel'),
        decideLabel: text(landing.expert?.offer?.decideLabel, 'expert.offer.decideLabel'),
        availableLabel: text(landing.expert?.offer?.availableLabel, 'expert.offer.availableLabel'),
        afterAccountLabel: text(
          landing.expert?.offer?.afterAccountLabel,
          'expert.offer.afterAccountLabel',
        ),
        ownerLabel: text(landing.expert?.offer?.ownerLabel, 'expert.offer.ownerLabel'),
      },
      monitoring: {
        title: text(landing.expert?.monitoring?.title, 'expert.monitoring.title'),
        answer: text(landing.expert?.monitoring?.answer, 'expert.monitoring.answer'),
        check: text(landing.expert?.monitoring?.check, 'expert.monitoring.check'),
        change: text(landing.expert?.monitoring?.change, 'expert.monitoring.change'),
        notify: text(landing.expert?.monitoring?.notify, 'expert.monitoring.notify'),
        status: text(landing.expert?.monitoring?.status, 'expert.monitoring.status'),
        actionContext: text(
          landing.expert?.monitoring?.actionContext,
          'expert.monitoring.actionContext',
        ),
        sourcesValue: text(
          landing.expert?.monitoring?.sourcesValue,
          'expert.monitoring.sourcesValue',
        ),
        changesValue: text(
          landing.expert?.monitoring?.changesValue,
          'expert.monitoring.changesValue',
        ),
        notificationValue: text(
          landing.expert?.monitoring?.notificationValue,
          'expert.monitoring.notificationValue',
        ),
      },
    },
    state: resolveLandingStateCopy(t, landing),
    labels: resolveLandingLabelsCopy(t, landing),
    footer: {
      description: text(landing.footer.companyDescription, 'footer.companyDescription'),
      legalAriaLabel: text(landing.footer.legalAriaLabel, 'footer.legalAriaLabel'),
      termsLabel: text(t.common.footer.termsOfService, 'common.footer.termsOfService'),
      privacyLabel: text(t.common.footer.privacyPolicy, 'common.footer.privacyPolicy'),
      contactLabel: text(t.common.footer.contact, 'common.footer.contact'),
      contactEmail: text(t.about?.supportSection.contactEmail, 'about.supportSection.contactEmail'),
      buyerLabel: text(owner.buyerDecode?.ctaLabel, 'ownerPricing.buyerDecode.ctaLabel'),
      allRightsReserved: text(landing.footer.allRightsReserved, 'footer.allRightsReserved'),
    },
  };
}
